import React from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const pages = {
  "terms": {
    title: "Terms and Conditions",
    updated: "1 May 2026",
    sections: [
      { h: "1. Acceptance of Terms", p: "By accessing or using Universal Tree's platform, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services." },
      { h: "2. Services", p: "Universal Tree connects customers with verified service professionals. We facilitate bookings but are not directly responsible for the quality of work performed by professionals." },
      { h: "3. User Accounts", p: "You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate information during registration." },
      { h: "4. Professional Verification", p: "All professionals undergo a verification process. Universal Tree reserves the right to reject or revoke verification at its discretion." },
      { h: "5. Prohibited Uses", p: "Users may not misuse the platform for fraudulent activities, harassment, or any unlawful purpose." },
      { h: "6. Limitation of Liability", p: "Universal Tree shall not be liable for any indirect, incidental, or consequential damages arising from the use of our platform." },
      { h: "7. Changes to Terms", p: "We reserve the right to update these terms at any time. Continued use after changes constitutes acceptance." },
      { h: "8. Governing Law", p: "These terms shall be governed by the laws of India." },
    ],
  },
  "privacy": {
    title: "Privacy Policy",
    updated: "1 May 2026",
    sections: [
      { h: "1. Information We Collect", p: "We collect personal information you provide during registration, including name, email, phone number, address, and payment details." },
      { h: "2. How We Use Information", p: "Your information is used to facilitate bookings, process payments, verify professionals, and improve our services." },
      { h: "3. Data Sharing", p: "We share necessary information with service professionals to enable bookings. We do not sell your personal data to third parties." },
      { h: "4. Location Data", p: "With your consent, we collect location data to show nearby professionals and enable live tracking of service appointments." },
      { h: "5. Data Security", p: "We implement reasonable security measures to protect your data. However, no method of transmission is 100% secure." },
      { h: "6. Your Rights", p: "You may access, update, or delete your personal information by contacting us. You may also withdraw consent for data processing." },
      { h: "7. Cookies", p: "We use essential cookies for authentication and functionality. We do not use tracking cookies for advertising." },
      { h: "8. Contact", p: "For privacy-related queries, contact us at hello@universaltree.io." },
    ],
  },
  "shipping": {
    title: "Shipping Policy",
    updated: "1 May 2026",
    sections: [
      { h: "1. Service Delivery", p: "Universal Tree provides on-demand home services. Service delivery times vary based on professional availability and location." },
      { h: "2. Booking Confirmation", p: "Once a professional accepts your booking, you will receive a confirmation with the expected service time." },
      { h: "3. Service Area", p: "We currently operate in select areas. Service availability is determined by your location and professional coverage." },
      { h: "4. Scheduling", p: "Service times are mutually agreed upon between customers and professionals through the platform." },
      { h: "5. Digital Services", p: "As a service platform, no physical goods are shipped. All services are delivered at the customer's location." },
    ],
  },
  "cancellation": {
    title: "Cancellation and Refunds",
    updated: "1 May 2026",
    sections: [
      { h: "1. Customer Cancellation", p: "Customers may cancel a booking before the professional accepts it with no penalty. After acceptance, cancellation may incur a fee." },
      { h: "2. Professional Cancellation", p: "If a professional cancels after accepting, we will assign an alternative professional at no extra cost." },
      { h: "3. Refund Eligibility", p: "Refunds are processed within 5–7 business days for eligible cancellations. Refunds are credited to the original payment method." },
      { h: "4. No-Show Policy", p: "If the professional does not arrive within the scheduled window, you are entitled to a full refund." },
      { h: "5. Disputes", p: "For any disputes regarding service quality, please contact us within 48 hours of service completion." },
      { h: "6. Registration Fee", p: "The professional registration fee (if any) is non-refundable once verification documents have been submitted." },
    ],
  },
};

export default function PolicyPages() {
  const { page } = useParams();
  const content = pages[page];
  if (!content) return null;

  return (
    <><Seo title={content?.title} description={content?.sections?.[0]?.p || "Universal Tree policy page"} />
    <div className="min-h-screen flex flex-col bg-[#FAF9F6]">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 md:py-20 w-full">
        <Link to="/" className="text-xs text-[#D9734E] hover:underline mb-4 inline-block">&larr; Back to home</Link>
        <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold mt-4">{content.updated}</p>
        <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-3">{content.title}</h1>
        <div className="mt-10 space-y-8">
          {content.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-display text-xl font-semibold text-[#1A1D1A] mb-2">{s.h}</h2>
              <p className="text-sm text-[#646964] leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
}
